import { ComponentPropsWithoutRef, useEffect, useRef } from 'react';
import { NavLink, NavLinkProps } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

interface SubNavRootProps extends ComponentPropsWithoutRef<'div'> {}
const SubNavRoot = (props: SubNavRootProps) => {
    const { children, className = '', ...restProps } = props;

    return (
        <div className={twMerge('flex flex-col gap-4', className)} {...restProps}>
            {children}
        </div>
    );
};

interface SubNavHeaderProps extends ComponentPropsWithoutRef<'div'> {}
const SubNavHeader = (props: SubNavHeaderProps) => {
    const { children, className = '', ...restProps } = props;

    return (
        <div
            className={twMerge(
                'flex min-h-10 flex-wrap items-center justify-between gap-4',
                className,
            )}
            {...restProps}
        >
            {children}
        </div>
    );
};

interface SubNavTitleProps extends ComponentPropsWithoutRef<'div'> {}
const SubNavTitle = (props: SubNavTitleProps) => {
    const { children, className = '', ...restProps } = props;

    return (
        <h1 className={twMerge('text-2xl font-medium leading-8', className)} {...restProps}>
            {children}
        </h1>
    );
};

interface SubNavActionsProps extends ComponentPropsWithoutRef<'div'> {}
const SubNavActions = (props: SubNavActionsProps) => {
    const { children, className = '', ...restProps } = props;

    return (
        <div className={twMerge('flex flex-wrap items-center gap-1.5', className)} {...restProps}>
            {children}
        </div>
    );
};

interface SubNavTabsProps extends ComponentPropsWithoutRef<'div'> {}
const SubNavTabs = (props: SubNavTabsProps) => {
    const { children, className = '', ...restProps } = props;

    return (
        <div
            className={twMerge('flex gap-3 overflow-auto border-b border-gray-200', className)}
            {...restProps}
        >
            {children}
        </div>
    );
};

interface SubNavTabItemProps extends NavLinkProps {}
const SubNavTabItem = (props: SubNavTabItemProps) => {
    const linkRef = useRef<HTMLAnchorElement>(null);
    const { children, to, ...restProps } = props;

    useEffect(() => {
        // ensures the selected tab item is visible on initial page load,
        // even if outside of horizontal scroll view
        if (linkRef.current?.hasAttribute('aria-current')) {
            linkRef.current.scrollIntoView({
                block: 'end',
                inline: 'nearest',
                behavior: 'smooth',
            });
        }
    }, []);

    return (
        <NavLink
            ref={linkRef}
            to={to}
            {...restProps}
            className={isActive =>
                twMerge(
                    isActive
                        ? 'relative text-blue-700 after:absolute after:bottom-0 after:left-0 after:block after:h-0.5 after:w-full after:bg-blue-500 after:content-[""]'
                        : 'text-gray-500',
                    'group/navlink min-w-max px-2 pb-2 pt-0.5 text-center font-medium leading-6',
                )
            }
        >
            {children}
        </NavLink>
    );
};

const Root = SubNavRoot;
const Header = SubNavHeader;
const Title = SubNavTitle;
const Actions = SubNavActions;
const Tabs = SubNavTabs;
const TabItem = SubNavTabItem;

export {
    Root,
    Header,
    Title,
    Actions,
    Tabs,
    TabItem,
    //
    SubNavRoot,
    SubNavHeader,
    SubNavTitle,
    SubNavActions,
    SubNavTabs,
    SubNavTabItem,
};
